import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Mail, Lock, User, AlertTriangle, ArrowLeft, Eye, EyeOff, Key } from 'lucide-react';
import fluencyLogo from '../../src/assets/preface-logo.png';
import { useAuth } from './AuthProvider'; // Import the auth context

const LoginPage = () => {
  const [activeView, setActiveView] = useState('login'); // login, register, forgotPassword
  const [credentials, setCredentials] = useState({ emailOrUsername: '', password: '' });
  const [registerInfo, setRegisterInfo] = useState({ 
    email: '', 
    username: '', 
    password: '', 
    confirmPassword: '',
    locationCode: '' // Added location code field
  });
  const [resetInfo, setResetInfo] = useState({ email: '', code: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: Enter email, 2: Enter code, 3: Reset password
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth(); // Use the auth context

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    // Clear errors/success when switching views
    setError('');
    setSuccess('');
  }, [activeView]);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, allows special characters
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(password);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      // Use the auth context's login method instead of direct fetch
      const result = await login({
        emailOrUsername: credentials.emailOrUsername,
        password: credentials.password
      });
      
      if (!result.success) {
        setError(result.error || 'Invalid credentials. Please try again.');
      }
      // No need to navigate - the useEffect will handle it when isAuthenticated changes
    } catch (err) {
      setError('Network error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!validateEmail(registerInfo.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    
    if (!validatePassword(registerInfo.password)) {
      setError('Password must be at least 8 characters with 1 uppercase letter, 1 lowercase letter, and 1 number.');
      return;
    }
    
    if (registerInfo.password !== registerInfo.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    // Validate location code is provided
    if (!registerInfo.locationCode.trim()) {
      setError('Location registration code is required.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: registerInfo.email,
          username: registerInfo.username,
          password: registerInfo.password,
          locationCode: registerInfo.locationCode // Send the location code to the server
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Registration successful! You can now log in.');
        setActiveView('login');
        setRegisterInfo({ email: '', username: '', password: '', confirmPassword: '', locationCode: '' });
      } else {
        setError(data.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('Network error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestPasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateEmail(resetInfo.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/request-password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetInfo.email })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResetStep(2);
        setSuccess('A verification code has been sent to your email.');
      } else {
        setError(data.error || 'Failed to send verification code. Please try again.');
      }
    } catch (err) {
      setError('Network error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyResetCode = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!resetInfo.code) {
      setError('Please enter the verification code.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/verify-reset-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: resetInfo.email,
          code: resetInfo.code 
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResetStep(3);
        setSuccess('Code verified. You can now reset your password.');
      } else {
        setError(data.error || 'Invalid code. Please try again.');
      }
    } catch (err) {
      setError('Network error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validatePassword(resetInfo.newPassword)) {
      setError('Password must be at least 8 characters with 1 uppercase letter, 1 lowercase letter, and 1 number.');
      return;
    }
    
    if (resetInfo.newPassword !== resetInfo.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: resetInfo.email,
          code: resetInfo.code,
          newPassword: resetInfo.newPassword 
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Password reset successful! You can now log in with your new password.');
        setActiveView('login');
        setResetInfo({ email: '', code: '', newPassword: '', confirmPassword: '' });
        setResetStep(1);
      } else {
        setError(data.error || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError('Network error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Rest of your component remains the same
  // ...

  return (
    <>
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">    
        <div className="flex justify-center mb-6">
          <img src={fluencyLogo} alt="Preface" className="h-16" />
        </div>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg flex items-start">
            <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-600 rounded-lg">
            {success}
          </div>
        )}
        
        {activeView === 'login' && renderLoginView()}
        {activeView === 'register' && renderRegisterView()}
        {activeView === 'forgotPassword' && renderForgotPasswordView()}
        </div>
      </div>
    </>
  );

  function renderLoginView() {
    return (
      <form onSubmit={handleLogin} className="space-y-4">
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Email or Username"
            value={credentials.emailOrUsername}
            onChange={(e) => setCredentials({ ...credentials, emailOrUsername: e.target.value })}
            className="w-full pl-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            required
            disabled={isLoading}
          />
        </div>
        
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={credentials.password}
            onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
            className="w-full pl-10 pr-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            required
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        
        <div className="text-right">
          <button
            type="button"
            onClick={() => setActiveView('forgotPassword')}
            className="text-sm text-teal-600 hover:underline"
          >
            Forgot password?
          </button>
        </div>
        
        <button
          type="submit"
          style={{ backgroundColor: '#2d6b78' }}
          className="w-full py-2 px-4 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="animate-spin mr-2 h-4 w-4" />
              Logging in...
            </>
          ) : (
            'Login'
          )}
        </button>
        
        <div className="text-center mt-4">
          <p className="text-gray-600">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={() => setActiveView('register')}
              className="text-teal-600 hover:underline"
            >
              Sign up
            </button>
          </p>
        </div>
      </form>
    );
  }

  function renderRegisterView() {
    return (
      <>
        <div className="mb-4">
          <button
            onClick={() => setActiveView('login')}
            className="flex items-center text-teal-600 hover:underline"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Login
          </button>
        </div>
        <form onSubmit={handleRegister} className="space-y-4">
          {/* Form fields remain the same */}
          {/* ... */}
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="email"
              placeholder="Email"
              value={registerInfo.email}
              onChange={(e) => setRegisterInfo({ ...registerInfo, email: e.target.value })}
              className="w-full pl-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Username"
              value={registerInfo.username}
              onChange={(e) => setRegisterInfo({ ...registerInfo, username: e.target.value })}
              className="w-full pl-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type={showRegisterPassword ? "text" : "password"}
              placeholder="Password"
              value={registerInfo.password}
              onChange={(e) => setRegisterInfo({ ...registerInfo, password: e.target.value })}
              className="w-full pl-10 pr-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowRegisterPassword(!showRegisterPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            >
              {showRegisterPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type={showRegisterConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              value={registerInfo.confirmPassword}
              onChange={(e) => setRegisterInfo({ ...registerInfo, confirmPassword: e.target.value })}
              className="w-full pl-10 pr-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowRegisterConfirmPassword(!showRegisterConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            >
              {showRegisterConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          
          {/* Location code field */}
          <div className="relative">
            <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Location Registration Code"
              value={registerInfo.locationCode}
              onChange={(e) => setRegisterInfo({ ...registerInfo, locationCode: e.target.value })}
              className="w-full pl-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              required
              disabled={isLoading}
            />
          </div>
          
          <div className="text-xs text-gray-600">
            <p className="mb-1">Password must be at least 8 characters with 1 uppercase letter, 1 lowercase letter, and 1 number.</p>
            <p>You must have a valid location registration code to create an account.</p>
          </div>
          
          <button
            type="submit"
            style={{ backgroundColor: '#2d6b78' }}
            className="w-full py-2 px-4 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin mr-2 h-4 w-4" />
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>
      </>
    );
  }

  function renderForgotPasswordView() {
    return (
      <>
        <div className="mb-4">
          <button
            onClick={() => {
              setActiveView('login');
              setResetStep(1);
            }}
            className="flex items-center text-teal-600 hover:underline"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Login
          </button>
        </div>
        
        {resetStep === 1 && (
          <form onSubmit={handleRequestPasswordReset} className="space-y-4">
            {/* Reset step 1 form */}
            <div className="text-sm text-gray-600 mb-4">
              Enter your email address and we'll send you a verification code to reset your password.
            </div>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="email"
                placeholder="Email"
                value={resetInfo.email}
                onChange={(e) => setResetInfo({ ...resetInfo, email: e.target.value })}
                className="w-full pl-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
                disabled={isLoading}
              />
            </div>
            
            <button
              type="submit"
              style={{ backgroundColor: '#2d6b78' }}
              className="w-full py-2 px-4 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  Sending...
                </>
              ) : (
                'Send Verification Code'
              )}
            </button>
          </form>
        )}
        
        {resetStep === 2 && (
          <form onSubmit={handleVerifyResetCode} className="space-y-4">
            {/* Reset step 2 form */}
            <div className="text-sm text-gray-600 mb-4">
              Enter the verification code sent to {resetInfo.email}
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Verification Code"
                value={resetInfo.code}
                onChange={(e) => setResetInfo({ ...resetInfo, code: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
                disabled={isLoading}
              />
            </div>
            
            <button
              type="submit"
              style={{ backgroundColor: '#2d6b78' }}
              className="w-full py-2 px-4 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  Verifying...
                </>
              ) : (
                'Verify Code'
              )}
            </button>
          </form>
        )}
        
        {resetStep === 3 && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            {/* Reset step 3 form */}
            <div className="text-sm text-gray-600 mb-4">
              Create a new password
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="New Password"
                value={resetInfo.newPassword}
                onChange={(e) => setResetInfo({ ...resetInfo, newPassword: e.target.value })}
                className="w-full pl-10 pr-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              >
                {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type={showConfirmNewPassword ? "text" : "password"}
                placeholder="Confirm New Password"
                value={resetInfo.confirmPassword}
                onChange={(e) => setResetInfo({ ...resetInfo, confirmPassword: e.target.value })}
                className="w-full pl-10 pr-10 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              >
                {showConfirmNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            
            <div className="text-xs text-gray-600">
              Password must be at least 8 characters with 1 uppercase letter, 1 lowercase letter, and 1 number.
            </div>
            
            <button
              type="submit"
              style={{ backgroundColor: '#2d6b78' }}
              className="w-full py-2 px-4 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        )}
      </>
    );
  }
};

export default LoginPage;